/**
 * "Use my current job from above" affordance.
 *
 * Catalog wires the I-130 (`sponsor.employment_history`) and the
 * I-864 (`sponsor.current_employment`) as separate field sets — they
 * map to different PDFs and have different shapes. But the *most
 * recent* row of the I-130 history IS the I-864 current employment
 * for a typical sponsor; asking the sponsor to type employer + title
 * twice was pure form-tribute. This module detects that overlap and
 * offers a one-click copy.
 *
 * Two pieces:
 *   - `findCurrentEmploymentSource` — pure: looks at every
 *     `sponsor.employment_history` instance and returns the one that
 *     is still ongoing (`date_to` blank or "Present"), or `null` if
 *     none matches.
 *   - `<SponsorEmploymentPrefillButton>` — surfaces the affordance
 *     just before the `sponsor.current_employment.status` anchor in
 *     the financials section. Click writes the copy back through
 *     `engine.setAnswer` for status / employer_one_name / employed_as.
 *     Hidden once the sponsor has already set their current status —
 *     the button is a shortcut, not a constant nag.
 */
import { useEffect, useState } from 'react';
import type { FieldId } from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';

export interface ICurrentEmploymentSource {
  instanceId: string;
  employerName: string;
  occupation: string;
}

export type TReadInstanceField = (
  fieldId: string,
  instanceId: string,
) => string | undefined;

/**
 * Pure: enumerate instances, return the first one whose `date_to`
 * reads as blank or "Present" AND whose `status` is employer-shaped
 * (employed / self_employed). Returns `null` when no current job
 * is present.
 *
 * The "first match" rule is intentional — there should only be one
 * ongoing job at a time. If a sponsor has accidentally entered two
 * overlapping "Present" rows, picking the first one (in catalog
 * instance order, which is the order they were added) is the
 * deterministic, stable choice.
 */
export function findCurrentEmploymentSource(
  instanceIds: readonly string[],
  readField: TReadInstanceField,
): ICurrentEmploymentSource | null {
  for (const id of instanceIds) {
    const dateTo = readField('sponsor.employment_history.date_to', id) ?? '';
    if (dateTo.trim().length > 0 && dateTo.trim().toLowerCase() !== 'present') {
      continue;
    }
    const status =
      readField('sponsor.employment_history.status', id) ?? '';
    if (status === 'unemployed' || status === 'retired' || status === 'student') {
      continue;
    }
    const employerName =
      readField('sponsor.employment_history.employer_name', id) ?? '';
    const occupation =
      readField('sponsor.employment_history.occupation', id) ?? '';
    // Skip if both fields are still blank — nothing to copy.
    if (employerName.trim().length === 0 && occupation.trim().length === 0) {
      continue;
    }
    return { instanceId: id, employerName, occupation };
  }
  return null;
}

const STATUS_FIELD_ID =
  'sponsor.current_employment.status' as FieldId;
const EMPLOYER_ONE_FIELD_ID =
  'sponsor.current_employment.employer_one_name' as FieldId;
const EMPLOYED_AS_FIELD_ID =
  'sponsor.current_employment.employed_as' as FieldId;

const HISTORY_GROUP_ID = 'sponsor.employment_history';

function readEngineText(engine: FormEngine, fieldId: FieldId, instanceId?: string): string {
  const v = engine.getAnswer(fieldId, instanceId);
  if (v?.type === 'text') return v.value;
  if (v?.type === 'select') return v.value;
  return '';
}

export interface ISponsorEmploymentPrefillButtonProps {
  engine: FormEngine;
  onTouchKey: (key: string) => void;
}

export function SponsorEmploymentPrefillButton(
  props: ISponsorEmploymentPrefillButtonProps,
): JSX.Element | null {
  const { engine, onTouchKey } = props;
  // Subscribe to engine revisions so the button appears / hides as the
  // sponsor fills (or clears) their employment history.
  const [, force] = useState(0);
  useEffect(() => engine.subscribe(() => force(n => n + 1)), [engine]);

  const state = engine.getRepeatableState(HISTORY_GROUP_ID);
  const instanceIds: string[] =
    state === null
      ? []
      : state.instances
          .map(i => i.instanceId)
          .filter((x): x is string => x !== undefined);
  const source = findCurrentEmploymentSource(
    instanceIds,
    (fieldId, instanceId) =>
      readEngineText(engine, fieldId as FieldId, instanceId),
  );
  if (source === null) return null;

  // Don't nag once the sponsor has answered current employment.
  const currentStatusSet = readEngineText(engine, STATUS_FIELD_ID).length > 0;
  if (currentStatusSet) return null;

  const handleClick = (): void => {
    engine.setAnswer(STATUS_FIELD_ID, { type: 'select', value: 'employed' });
    onTouchKey(STATUS_FIELD_ID);
    if (source.employerName.trim().length > 0) {
      engine.setAnswer(EMPLOYER_ONE_FIELD_ID, {
        type: 'text',
        value: source.employerName,
      });
      onTouchKey(EMPLOYER_ONE_FIELD_ID);
    }
    if (source.occupation.trim().length > 0) {
      engine.setAnswer(EMPLOYED_AS_FIELD_ID, {
        type: 'text',
        value: source.occupation,
      });
      onTouchKey(EMPLOYED_AS_FIELD_ID);
    }
  };

  const label = source.employerName.trim().length > 0
    ? `Use my current job (${source.employerName})`
    : 'Use my current job from above';

  return (
    <button
      type="button"
      onClick={handleClick}
      data-sponsor-employment-prefill
      style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        color: '#1e40af',
        borderRadius: '8px',
        padding: '8px 14px',
        fontSize: '0.88rem',
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      ↳ {label}
    </button>
  );
}
